import CustomButton, {IconButton, OutlinedButton} from "../../../components/CustomButton";
import Input from "../../../components/form/Input";
import React, {useEffect, useState} from "react";
import {RadioGroup, RadioItem} from "../../../components/form/RadioGroup";
import {
  bulkUpdate,
  fetchAreasByCity,
  fetchCitiesByProvince,
  fetchProvinces,
} from "../../../redux/slices/locationSlice";
import {useDispatch, useSelector} from "react-redux";
import LabeledSelect from "../../../components/selects/LabeledSelect";
import {LOCATION_TYPE} from "../../focusItems/enums";
import {cloneDeep, isEqual} from "lodash";
import Chip from "../../../components/form/Chip";
import UnitSelector, {unit} from "./UnitSelector";
import {
  ifNaNReturnElseUndefined, ifNaNReturnUndefined,
  mapAndFilterUndefinedValues,
  mapAndParseObjectValueToNumberIfPossible,
  removeKeysFromObjectWithEmptyOrNullOrUndefinedValue
} from "../../../helpers/objectHelper";
import {isInputFiledValid} from "../../../helpers/stringHelper";
import {useNavigate} from "react-router-dom";
import useDidMount from "../../../hooks/useDidMount";

const initialBulkPayload = {
  provinces: [],
  areas: [],
  cities: [],
  updates: {
    max_shippable_weight: "",
    discount_percentage: "",
    price: {
      discounts: [
        {
          max: "",
          min: "",
          discount_amt: ""
        },
      ],
      rate: ""
    },
    weight: {
      charges: [
        {
          max: "",
          inc_factor: "",
          // unit: unit[0].value,
          rate: "",
          min: ""
        }
      ],
      max_shipping_charge: "",
      weight_increment_threshold: ""
    }
  }
}

const BulkUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const {province, city, area, toggle} = useSelector((state => state.location))

  // for dropdown
  const [areaOptions, setAreaOptions] = useState([])
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  // for currently selected location either province or city or area
  const [selectedLocation, setSelectedLocation] = useState(LOCATION_TYPE[0]);

  // for payload
  const [bulkPayload, setBulkPayload] = useState(initialBulkPayload);

  // for value selected from dropdown
  const [selectedArea, setSelectedArea] = useState(undefined);
  const [selectedCity, setSelectedCity] = useState(undefined);
  const [selectedProvince, setSelectedProvince] = useState(undefined);

  useEffect(() => {
    dispatch(fetchProvinces())
  }, [])

  useEffect(() => {
    setSelectedProvince(undefined);
    setSelectedArea(undefined);
    setSelectedCity(undefined)
    setBulkPayload({provinces: [], areas: [], cities: [], ...bulkPayload})
  }, [selectedLocation]);

  useDidMount(() => {
    navigate("/location-service", {replace: true})
  }, [toggle])

  useEffect(() => {
    if (province.data.length) {
      setProvinceOptions(createDropdownOptions(province.data));
    }
    if (city.data.length) {
      setCityOptions(createDropdownOptions(city.data));
    }
    if (area.data.length) {
      setAreaOptions(createDropdownOptions(area.data))
    }

  }, [province, city, area])

  const fetchLocation = (type, id) => {
    if (type === "CITY") {
      dispatch(fetchCitiesByProvince({provinceId: id}))
    } else if (type === "AREA") {
      dispatch(fetchAreasByCity({cityId: id}))
    }
  }

  const createDropdownOptions = (payload) => {
    return payload.map((data) => {
      return {
        value: data.id,
        label: data.name
      }
    })
  }

  const hasLocationInCollection = (collection, location) => {
    return collection.find((c) => c === location) !== undefined
  }

  const applySelectedLocation = () => {
    if (selectedLocation === LOCATION_TYPE[0] && !hasLocationInCollection(bulkPayload.provinces, selectedProvince)) {
      bulkPayload.provinces.push(selectedProvince)
      setBulkPayload({...bulkPayload})
    } else if (selectedLocation === LOCATION_TYPE[1] && !hasLocationInCollection(bulkPayload.cities, selectedCity)) {
      bulkPayload.cities.push(selectedCity)
      setBulkPayload({...bulkPayload})
    } else if (selectedLocation === LOCATION_TYPE[2] && !hasLocationInCollection(bulkPayload.areas, selectedArea)) {
      bulkPayload.areas.push(selectedArea)
      setBulkPayload({...bulkPayload})
    }
  }

  const SelectedLocationChip = ({location, type}) => {
    return (
      <Chip label={location.label} onClick={() => {
        if (type === "Province") {
          setBulkPayload({
            ...bulkPayload,
            provinces: bulkPayload.provinces.filter((province) => province.value !== location.value)
          })
        }
        if (type === "City") {
          setBulkPayload({...bulkPayload, cities: bulkPayload.cities.filter((city) => city.value !== location.value)})
        }
        if (type === "Area") {
          setBulkPayload({...bulkPayload, areas: bulkPayload.areas.filter((area) => area.value !== location.value)})
        }
      }}/>
    )
  }

  const isDiscountFormValid = () => {
    return bulkPayload.updates?.price?.discounts.some((d) => !isInputFiledValid(d.min) || !isInputFiledValid(d.max) || !isInputFiledValid(d.discount_amt));
  };

  const isChargeFormValid = () => {
    return bulkPayload.updates.weight?.charges.some((d) => !isInputFiledValid(d.min) || !isInputFiledValid(d.max) || !isInputFiledValid(d.inc_factor) || !isInputFiledValid(d.rate)
    );
  };

  const isFormValid = () => {
    return (selectedLocation === LOCATION_TYPE[0] ?
      !bulkPayload.provinces.length :
      selectedLocation === LOCATION_TYPE[1] ? !bulkPayload.cities.length : !bulkPayload.areas.length)
  }

  return <div className="m-5 p-5 bg-white">
    <RadioGroup label="Apply For"> {LOCATION_TYPE.map((location) => {
      return <RadioItem key={location.label} name="location" label={location.label} value={selectedLocation.value}
                        isChecked={selectedLocation === location}
                        onChanged={() => setSelectedLocation(location)}/>
    })} </RadioGroup>

    <div className="flex space-x-2 items-center">
      <LabeledSelect
        label="Province"
        placeholder="Select Province"
        options={provinceOptions}
        loading={province.loading}
        value={selectedProvince}
        onChange={(selectedOption) => {
          setSelectedProvince(selectedOption)
          if (!isEqual(LOCATION_TYPE[0], selectedLocation)) {
            fetchLocation("CITY", selectedOption.value)
            //clear selected city and area
            setSelectedCity(undefined);
            setSelectedArea(undefined);
          }
        }}
      />
      {
        (isEqual(LOCATION_TYPE[1], selectedLocation) || isEqual(LOCATION_TYPE[2], selectedLocation)) &&
        <LabeledSelect
          label="City"
          placeholder="Select City"
          options={cityOptions}
          loading={province.loading || city.loading}
          value={selectedCity}
          isDisabled={!selectedProvince || city.loading}
          onChange={(selectedOption) => {
            setSelectedCity(selectedOption)
            if (!isEqual(LOCATION_TYPE[1], selectedLocation)) {
              fetchLocation("AREA", selectedOption.value)
              //clear selected area
              setSelectedArea(undefined);
            }
          }
          }
        />
      }
      {
        isEqual(LOCATION_TYPE[2], selectedLocation) &&
        <LabeledSelect
          label="Area"
          placeholder="Select Area"
          options={areaOptions}
          loading={province.loading || city.loading || area.loading}
          value={selectedArea}
          isDisabled={!selectedCity || area.loading}
          onChange={(selectedOption) => setSelectedArea(selectedOption)}
        />
      }
      <CustomButton className="mt-2 h-min"
                    disabled={selectedLocation === LOCATION_TYPE[0] ? selectedProvince === undefined : selectedLocation === LOCATION_TYPE[1] ? (selectedProvince === undefined || selectedCity === undefined) : (selectedProvince === undefined || selectedCity === undefined || selectedArea === undefined)}
                    onClick={applySelectedLocation}>Apply</CustomButton>
    </div>

    {/*selected province/cities/areas section*/}
    <div>
      {selectedLocation === LOCATION_TYPE[0] && <div className="p-2 bg-secondary-100 space-y-2">
        <h1>Selected Provinces</h1>
        <div className="flex flex-wrap">
          {
            bulkPayload.provinces.map((province, index) => <SelectedLocationChip key={index} location={province}
                                                                                 type="Province"/>)
          }
        </div>
      </div>}

      {selectedLocation === LOCATION_TYPE[1] && <div className="p-2 bg-secondary-100 space-y-2">
        <h1>Selected Cities</h1>
        <div className="flex flex-wrap¬">
          {
            bulkPayload.cities.map((province, index) => <SelectedLocationChip key={index} location={province}
                                                                              type="City"/>)
          }
        </div>
      </div>}
      {selectedLocation === LOCATION_TYPE[2] && <div className="p-2 bg-secondary-100 space-y-2">
        <h1>Selected Areas</h1>
        <div className="flex flex-wrap">
          {
            bulkPayload.areas.map((province, index) => <SelectedLocationChip key={index} location={province}
                                                                             type={"Area"}/>)
          }
        </div>
      </div>}
    </div>

    {/*area form section*/}
    <div className="border-4 p-2 my-2 rounded-xl">
      <div className="flex  space-x-2">
        <Input label="Max Shippable Weight" placeholder="Ex: 10" value={bulkPayload.updates.max_shippable_weight}
               onChange={(e) => {
                 bulkPayload.updates.max_shippable_weight = e.target.value
                 setBulkPayload({...bulkPayload})
               }}/>
        <Input label="Discount Percentage" placeholder="Ex: 10" value={bulkPayload.updates.discount_percentage}
               onChange={(e) => {
                 bulkPayload.updates.discount_percentage = e.target.value
                 setBulkPayload({...bulkPayload})

               }}/>
      </div>
      <div className="flex space-x-2">
        {/*price section*/}
        <div>
          <h1>Price</h1>
          <Input label="Rate" placeholder="Ex: 10" value={bulkPayload.updates.price?.rate} onChange={(e) => {
            bulkPayload.updates.price.rate = e.target.value
            setBulkPayload({...bulkPayload})
          }}/>
          <p>Discounts</p>
          <div className="space-y-2">
            {
              bulkPayload.updates.price?.discounts.map((d, index) =>
                <div key={`bulk-updates-price-discount-${d.toString()}-${index}`}
                     className="relative p-4 pt-8 mb-4 border bg-secondary-100 rounded-md">
                  {bulkPayload.updates.price?.discounts.length > 1 && (
                    <div className="absolute top-2 right-2">
                      <IconButton
                        onClick={() => {
                          bulkPayload.updates.price.discounts = bulkPayload.updates.price?.discounts.filter(
                            (value) => value !== d
                          )
                          setBulkPayload({...bulkPayload})
                        }}
                      />
                    </div>
                  )}
                  <div className="flex space-x-2">
                    <Input placeholder="Min Amt" value={d.min} onChange={(e) => {
                      d.min = e.target.value;
                      setBulkPayload({...bulkPayload})
                    }}/>
                    <Input placeholder="Max Amt" value={d.max} onChange={(e) => {
                      d.max = e.target.value;
                      setBulkPayload({...bulkPayload})

                    }}/>
                    <Input placeholder="Dis. Amt" value={d.discount_amt} onChange={(e) => {
                      d.discount_amt = e.target.value;
                      setBulkPayload({...bulkPayload})

                    }}/>
                  </div>
                </div>
              )
            }
            {/*add more button*/}
            <div className="flex justify-end">
              <OutlinedButton
                disabled={isDiscountFormValid()}
                onClick={() => {
                  bulkPayload.updates.price?.discounts.push({
                    min: '',
                    max: '',
                    discount_amt: '',
                  });
                  setBulkPayload({...bulkPayload})

                }}
              >
                + Add more
              </OutlinedButton>
            </div>
          </div>
        </div>
        <div className="w-1 bg-gray-200"/>
        {/*weight section*/}
        <div>
          <h1>Weight</h1>
          <div className="flex space-x-2">
            <Input label="Max Shipping Charge" placeholder="Ex: 10"
                   value={bulkPayload.updates?.weight?.max_shipping_charge}
                   onChange={(e) => {
                     bulkPayload.updates.weight.max_shipping_charge = e.target.value
                     setBulkPayload({...bulkPayload})

                   }}/>
            <Input label="Weight Increment Threshold" placeholder="Ex: 10"
                   value={bulkPayload.updates?.weight?.weight_increment_threshold}
                   onChange={(e) => {
                     bulkPayload.updates.weight.weight_increment_threshold = e.target.value
                     setBulkPayload({...bulkPayload})

                   }}/>
          </div>
          <p>Charges</p>
          <div className="space-y-2">
            {
              bulkPayload.updates.weight?.charges.map((c, index) =>
                <div key={`bulk-update-weight-charge-${c.toString()}-${index}`}
                     className="relative p-4 pt-8 mb-4 border bg-secondary-100 rounded-md">
                  {bulkPayload.updates.weight?.charges.length > 1 && (
                    <div className="absolute top-2 right-2">
                      <IconButton
                        onClick={() => {
                          bulkPayload.updates.weight.charges = bulkPayload.updates.weight?.charges.filter(
                            (value) => value !== c
                          );
                          setBulkPayload({...bulkPayload})
                        }}
                      />
                    </div>
                  )}
                  <div className="flex space-x-2">
                    <Input placeholder="Min Weight" value={c.min} onChange={(e) => {
                      c.min = e.target.value
                      setBulkPayload({...bulkPayload})

                    }}/>
                    <Input placeholder="Max Weight" value={c.max} onChange={(e) => {
                      c.max = e.target.value
                      setBulkPayload({...bulkPayload})

                    }}/>
                    <Input placeholder="Rate" value={c.rate} onChange={(e) => {
                      c.rate = e.target.value
                      setBulkPayload({...bulkPayload})

                    }}/>
                    <Input placeholder="Inc factor" value={c.inc_factor} onChange={(e) => {
                      c.inc_factor = e.target.value
                      setBulkPayload({...bulkPayload})
                    }}/>
                    <UnitSelector onChange={(e) => {
                      c.unit = e.value
                      setBulkPayload({...bulkPayload})
                    }} selectedUnit={unit[0].value}
                    />
                  </div>
                </div>
              )
            }
            <div className="flex justify-end">
              <OutlinedButton
                disabled={isChargeFormValid()}
                onClick={() => {
                  bulkPayload.updates.weight?.charges.push({
                    min: '',
                    max: '',
                    rate: '',
                    inc_factor: '',
                    // unit: '',
                  });
                  setBulkPayload({...bulkPayload});
                }}
              >
                + Add more
              </OutlinedButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CustomButton
      disabled={isFormValid()}
      onClick={() => {
        const provinces = bulkPayload.provinces.map((p) => p.value)
        const cities = bulkPayload.cities.map((c) => c.value)
        const areas = bulkPayload.areas.map((a) => a.value)

        const newBulkPayload = {...bulkPayload, provinces: provinces, cities: cities, areas: areas}

        if (selectedLocation === LOCATION_TYPE[0]) {
          delete newBulkPayload["cities"]
          delete newBulkPayload["areas"]
        } else if (selectedLocation === LOCATION_TYPE[1]) {
          delete newBulkPayload["provinces"]
          delete newBulkPayload["areas"]
        } else {
          delete newBulkPayload["provinces"]
          delete newBulkPayload["cities"]
        }

        const deepCopyPayload = cloneDeep(newBulkPayload)
        const payload = removeKeysFromObjectWithEmptyOrNullOrUndefinedValue(deepCopyPayload)
        const finalPayload = {
          ...payload,
          updates: {
            discount_percentage: ifNaNReturnUndefined(parseFloat(payload?.updates?.discount_percentage)),
            max_shippable_weight: ifNaNReturnUndefined(parseFloat(payload?.updates?.max_shippable_weight)),
            weight: {
              max_shipping_charge: ifNaNReturnUndefined(parseFloat(payload?.updates?.weight?.max_shipping_charge)),
              weight_increment_threshold: ifNaNReturnUndefined(parseFloat(payload?.updates?.weight?.weight_increment_threshold)),
              charges: mapAndParseObjectValueToNumberIfPossible(payload?.updates?.weight?.charges)
            },
            price: {
              rate: ifNaNReturnUndefined(parseFloat(payload?.updates?.price?.rate)),
              discounts: mapAndParseObjectValueToNumberIfPossible(payload?.updates?.price?.discounts)
            }
          }
        }
        dispatch(bulkUpdate(removeKeysFromObjectWithEmptyOrNullOrUndefinedValue(finalPayload)))
      }}>Update</CustomButton>
  </div>;
}

export default BulkUpdate;